import * as XLSX from 'xlsx'
import type { OrderResponse } from '@/services/ordersApi'

export const STATUS_LABELS: Record<string, string> = {
  pending_confirmation: 'Pendiente de confirmación',
  confirmed: 'Confirmada',
  in_progress: 'En proceso',
  ready: 'Lista para entregar',
  completed: 'Completada',
  cancelled: 'Cancelada',
  draft: 'Borrador',
}

export interface OrderExcelRow {
  'N° Orden': string
  'Fecha': string
  'Estado': string
  'Cliente': string
  'Teléfono': string
  'Correo': string
  'Total': number
  'Moneda': string
  'Método de Entrega': string
  'Dirección': string
  'Referencia': string
  'Productos': string
  'Cantidad Items': number
  'Canal': string
  'Notas': string
}

export function formatOrdersForExcel(orders: OrderResponse[]): OrderExcelRow[] {
  return orders.map((order) => {
    const itemsSummary = (order.items || [])
      .map((item) => `${item.name} (x${item.quantity})`)
      .join(', ')

    const fulfillmentMethodLabel =
      order.fulfillmentMethod === 'delivery'
        ? 'Delivery'
        : order.fulfillmentMethod === 'pickup' || order.fulfillmentMethod === 'store_pickup'
        ? 'Retiro en tienda'
        : order.fulfillmentMethod === 'cash_on_delivery'
        ? 'Cobro destino'
        : order.fulfillmentMethod === 'shipping'
        ? 'Envío nacional'
        : order.fulfillmentMethod || 'No especificado'

    const totalAmount = (order.totalCents || 0) / 100

    return {
      'N° Orden': order.orderNumber || order.id.slice(0, 8),
      'Fecha': new Date(order.createdAt).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      'Estado': STATUS_LABELS[order.status] || order.status,
      'Cliente': order.customerName || order.contact?.name || 'Sin nombre',
      'Teléfono': order.customerPhone || order.contact?.phone || order.contact?.waContact || '',
      'Correo': order.customerEmail || order.contact?.email || '',
      'Total': totalAmount,
      'Moneda': order.currency || 'USD',
      'Método de Entrega': fulfillmentMethodLabel,
      'Dirección': order.fulfillmentAddress || '',
      'Referencia': order.fulfillmentReference || '',
      'Productos': itemsSummary,
      'Cantidad Items': (order.items || []).reduce((acc, it) => acc + (it.quantity || 1), 0),
      'Canal': order.channelInstance?.instanceName || order.source || 'Manual',
      'Notas': order.notes || '',
    }
  })
}

export function exportOrdersToExcel(orders: OrderResponse[], filenamePrefix = 'ordenes'): void {
  if (!orders || orders.length === 0) {
    throw new Error('No hay órdenes para exportar')
  }

  const rows = formatOrdersForExcel(orders)

  // Create worksheet from data
  const worksheet = XLSX.utils.json_to_sheet(rows)

  // Adjust column widths
  worksheet['!cols'] = [
    { wch: 14 }, // N° Orden
    { wch: 18 }, // Fecha
    { wch: 20 }, // Estado
    { wch: 24 }, // Cliente
    { wch: 16 }, // Teléfono
    { wch: 22 }, // Correo
    { wch: 12 }, // Total
    { wch: 8 },  // Moneda
    { wch: 20 }, // Método de Entrega
    { wch: 30 }, // Dirección
    { wch: 20 }, // Referencia
    { wch: 40 }, // Productos
    { wch: 14 }, // Cantidad Items
    { wch: 18 }, // Canal
    { wch: 30 }, // Notas
  ]

  // Create workbook
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Órdenes')

  // Generate file name with current date
  const dateStr = new Date().toISOString().slice(0, 10)
  const filename = `${filenamePrefix}_${dateStr}.xlsx`

  XLSX.writeFile(workbook, filename)
}
